<script setup>
import { nextTick, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: 'ex) 12:00'
    },
    disabled: {
        type: Boolean,
        default: false
    },
    readonly: {
        type: Boolean,
        default: false
    },
    visible: {
        type: Boolean,
        default: true
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    padding: {
        type: String,
        default: '0.75rem 1rem'
    }
});

const emit = defineEmits(['update:modelValue', 'input']);

const inputValue = ref(props.modelValue || '');
const errMessage = ref(props.errmsg);
const isInvalid = ref(false);

watch(
    () => props.modelValue,
    (val) => {
        inputValue.value = val || '';
    }
);

watch(
    () => props.errmsg,
    (val) => {
        errMessage.value = val || '';
    }
);

// 입력 시
const onTimeInput = (event) => {
    errMessage.value = '';
    const input = event.target;
    const prevCursor = input.selectionStart;

    const raw = input.value.replace(/\D/g, '').slice(0, 4);

    const leftPart = input.value.slice(0, prevCursor);
    const digitsBeforeCursor = leftPart.replace(/\D/g, '').length;

    let formatted = raw.length <= 2 ? raw : `${raw.slice(0, 2)}:${raw.slice(2)}`;

    inputValue.value = formatted;
    emit('update:modelValue', formatted);
    emit('input', formatted);

    nextTick(() => {
        const newCursor = digitsBeforeCursor <= 2 ? digitsBeforeCursor : digitsBeforeCursor + 1;
        const safeCursor = Math.min(newCursor, formatted.length);
        input.setSelectionRange(safeCursor, safeCursor);
    });
};

// 포커스 해제 시
const onTimeBlur = () => {
    if (!inputValue.value) {
        isInvalid.value = false; // 빈 값은 정상 처리
        return;
    }

    const [hStr, mStr] = inputValue.value.split(':');

    if (!hStr || !mStr || hStr.length !== 2 || mStr.length !== 2) return;

    const hour = parseInt(hStr, 10);
    const minute = parseInt(mStr, 10);

    const isHourInvalid = isNaN(hour) || hour < 0 || hour > 23;
    const isMinuteInvalid = isNaN(minute) || minute < 0 || minute > 59;

    let validated = '';

    if (isHourInvalid || isMinuteInvalid) {
        validated = '00:00';
        errMessage.value = '잘못 된 시간형식입니다.';
        isInvalid.value = true; // 잘못된 입력
    } else {
        validated = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

        isInvalid.value = false; // 올바른 입력
    }

    inputValue.value = validated;
    emit('update:modelValue', validated);
    emit('input', validated);
};

const clearInput = () => {
    inputValue.value = '00:00';
    errMessage.value = '';
    isInvalid.value = false;
    emit('update:modelValue', inputValue.value);
    emit('input', inputValue.value);
};
</script>

<template>
    <div v-if="visible" class="w-full flex flex-col">
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="errMessage" class="validation-message">{{ errMessage }}</span>
        </label>

        <div class="relative flex items-center w-full h-12">
            <input
                v-model="inputValue"
                :inputId="inputId"
                type="text"
                class="p-inputtext custom-time-input w-full h-[2.5rem] ps-5"
                :class="{ 'error-border': isInvalid }"
                :placeholder="placeholder"
                :readonly="readonly"
                :disabled="disabled"
                @input="onTimeInput"
                @blur="onTimeBlur"
            />
            <button v-if="inputValue && !readonly" class="clear-button" @click="clearInput" aria-label="Clear">&times;</button>
        </div>
    </div>
</template>

<style scoped>
.label-font {
    font-family: 'SCoreDream600';
    font-size: 0.875rem;
    color: #111827;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.custom-time-input {
    /* padding: 0.75rem; 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    font-size: 0.9rem;
    color: black;
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-time-input:focus {
    border-color: #0038ea !important;
    outline: none;
}

.custom-time-input[readonly] {
    background-color: whitesmoke;
    cursor: default;
}

.custom-time-input[readonly]:focus {
    border-color: #d1d5db !important;
    outline: none;
}

.clear-button {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    cursor: pointer;
}

.clear-button:hover {
    color: #111827;
}

.error-border {
    border: 1px solid red !important;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}
</style>
